import React, { useState } from "react";
import { PageHeader, Section, Field, Input, Textarea } from "@/components/Common";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import api from "@/lib/api";
import PartnersStrip from "@/components/PartnersStrip";

function useFormSubmit(endpoint, successMsg) {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (payload, reset) => {
    setLoading(true);
    try {
      await api.post(endpoint, payload);
      toast.success(successMsg);
      setDone(true);
      reset && reset();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally { setLoading(false); }
  };
  return { submit, loading, done };
}

export function Volunteer() {
  const [f, setF] = useState({ full_name: "", email: "", phone: "", interests: "", availability: "", message: "" });
  const { submit, loading, done } = useFormSubmit("/volunteers", "Thank you! We'll be in touch soon.");
  return (
    <div data-testid="volunteer-page">
      <PageHeader eyebrow="Volunteer" title="Bring your skills, time, and heart to BCOL."
        subtitle="Volunteers are the foundation of our work. Tell us about you — we'll connect you to the right opportunity." />
      <Section>
        <form onSubmit={(e) => { e.preventDefault(); submit(f); }} className="max-w-3xl mx-auto card-soft p-8 space-y-6">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Full Name *"><Input required value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} data-testid="volunteer-name" /></Field>
            <Field label="Email *"><Input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="volunteer-email" /></Field>
            <Field label="Phone"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="volunteer-phone" /></Field>
            <Field label="Availability"><Input placeholder="Weekends, evenings..." value={f.availability} onChange={(e) => setF({ ...f, availability: e.target.value })} data-testid="volunteer-availability" /></Field>
          </div>
          <Field label="Areas of interest"><Input placeholder="Events, education, mentorship, outreach..." value={f.interests} onChange={(e) => setF({ ...f, interests: e.target.value })} data-testid="volunteer-interests" /></Field>
          <Field label="Why do you want to volunteer with BCOL?"><Textarea rows={4} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="volunteer-message" /></Field>
          <button disabled={loading || done} className="btn-primary" data-testid="volunteer-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {done ? "Submitted" : loading ? "Submitting..." : "Submit Application"}
          </button>
        </form>
      </Section>
    </div>
  );
}

export function Membership() {
  const [f, setF] = useState({ full_name: "", email: "", phone: "", address: "", membership_type: "Individual", message: "" });
  const { submit, loading, done } = useFormSubmit("/memberships", "Membership request received!");
  return (
    <div data-testid="membership-page">
      <PageHeader eyebrow="Membership" title="Join the BCOL community." subtitle="Become a member and stay connected to events, programs, and our shared mission." />
      <Section>
        <form onSubmit={(e) => { e.preventDefault(); submit(f); }} className="max-w-3xl mx-auto card-soft p-8 space-y-6">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Full Name *"><Input required value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} data-testid="membership-name" /></Field>
            <Field label="Email *"><Input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="membership-email" /></Field>
            <Field label="Phone"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="membership-phone" /></Field>
            <Field label="Membership type">
              <select className="w-full bg-white border border-[var(--bcol-border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--bcol-navy)]"
                value={f.membership_type} onChange={(e) => setF({ ...f, membership_type: e.target.value })} data-testid="membership-type">
                <option>Individual</option><option>Family</option><option>Student</option><option>Senior</option><option>Lifetime</option>
              </select>
            </Field>
          </div>
          <Field label="Address"><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="membership-address" /></Field>
          <Field label="Anything else we should know?"><Textarea rows={3} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="membership-message" /></Field>
          <button disabled={loading || done} className="btn-primary" data-testid="membership-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {done ? "Submitted" : loading ? "Submitting..." : "Request Membership"}
          </button>
        </form>
      </Section>
    </div>
  );
}

export function Nominate() {
  const [f, setF] = useState({ nominator_name: "", nominator_email: "", nominee_name: "", school: "", achievement: "", graduation_year: "", additional: "" });
  const { submit, loading, done } = useFormSubmit("/nominations", "Nomination received!");
  return (
    <div data-testid="nominate-page">
      <PageHeader eyebrow="Graduate Recognition" title="Nominate a graduate." subtitle="Help us celebrate the achievements of community youth — every story matters." />
      <Section>
        <form onSubmit={(e) => { e.preventDefault(); submit(f); }} className="max-w-3xl mx-auto card-soft p-8 space-y-6">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Your name *"><Input required value={f.nominator_name} onChange={(e) => setF({ ...f, nominator_name: e.target.value })} data-testid="nominate-nominator-name" /></Field>
            <Field label="Your email *"><Input type="email" required value={f.nominator_email} onChange={(e) => setF({ ...f, nominator_email: e.target.value })} data-testid="nominate-nominator-email" /></Field>
            <Field label="Nominee's name *"><Input required value={f.nominee_name} onChange={(e) => setF({ ...f, nominee_name: e.target.value })} data-testid="nominate-nominee-name" /></Field>
            <Field label="School / Institution *"><Input required value={f.school} onChange={(e) => setF({ ...f, school: e.target.value })} data-testid="nominate-school" /></Field>
            <Field label="Graduation year *"><Input required value={f.graduation_year} onChange={(e) => setF({ ...f, graduation_year: e.target.value })} data-testid="nominate-year" /></Field>
          </div>
          <Field label="Achievement / why nominate *"><Textarea required rows={4} value={f.achievement} onChange={(e) => setF({ ...f, achievement: e.target.value })} data-testid="nominate-achievement" /></Field>
          <Field label="Additional info"><Textarea rows={3} value={f.additional} onChange={(e) => setF({ ...f, additional: e.target.value })} data-testid="nominate-additional" /></Field>
          <button disabled={loading || done} className="btn-primary" data-testid="nominate-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {done ? "Submitted" : loading ? "Submitting..." : "Submit Nomination"}
          </button>
        </form>
      </Section>
    </div>
  );
}

export function Contact() {
  const [f, setF] = useState({ name: "", email: "", subject: "", message: "" });
  const { submit, loading, done } = useFormSubmit("/contact", "Message sent! We'll respond shortly.");
  return (
    <div data-testid="contact-page">
      <PageHeader eyebrow="Contact" title="Let's talk." subtitle="Questions, partnerships, press, or how to get involved — we'd love to hear from you." />
      <Section>
        <div className="grid lg:grid-cols-12 gap-10">
          <form onSubmit={(e) => { e.preventDefault(); submit(f); }} className="lg:col-span-7 card-soft p-8 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Name *"><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="contact-name" /></Field>
              <Field label="Email *"><Input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="contact-email" /></Field>
            </div>
            <Field label="Subject *"><Input required value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="contact-subject" /></Field>
            <Field label="Message *"><Textarea required rows={5} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="contact-message" /></Field>
            <button disabled={loading || done} className="btn-primary" data-testid="contact-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {done ? "Sent" : loading ? "Sending..." : "Send Message"}
            </button>
          </form>
          <aside className="lg:col-span-5 space-y-5">
            <div className="card-soft p-7">
              <div className="eyebrow mb-2">Headquarters</div>
              <p className="text-[var(--bcol-navy)] font-display text-xl">Columbus, Ohio · USA</p>
              <p className="text-[var(--bcol-muted)] text-sm mt-2">Meetings by appointment. Reach out for community office hours.</p>
            </div>
            <div className="card-soft p-7">
              <div className="eyebrow mb-2">Email & Phone</div>
              <p className="text-sm">info@bassecoteohiolagni.org</p>
              <p className="text-sm text-[var(--bcol-muted)] mt-1">+1 (614) 588-5325</p>
            </div>
            <div className="card-soft p-7 bg-[var(--bcol-navy)] text-white">
              <div className="eyebrow text-[var(--bcol-gold)] mb-2">Press & Partnerships</div>
              <p className="text-sm text-white/80">For media inquiries or partnership opportunities, please email <b>info@bassecoteohiolagni.org</b>.</p>
            </div>
          </aside>
        </div>
      </Section>
    </div>
  );
}

export function Sponsorship() {
  const [f, setF] = useState({ company_name: "", contact_name: "", email: "", phone: "", tier: "Gold", message: "" });
  const { submit, loading, done } = useFormSubmit("/sponsors", "Thank you! Our partnerships team will reach out.");
  const tiers = [
    { name: "Bronze", price: "$1,000", perks: ["Logo on website", "Recognition at one event", "Annual report mention"] },
    { name: "Silver", price: "$2,500", perks: ["Bronze benefits", "Social media features (3x)", "Gala table for 4"] },
    { name: "Gold", price: "$5,000", perks: ["Silver benefits", "Speaking opportunity", "Gala table for 8"], highlight: true },
    { name: "Platinum", price: "$10,000+", perks: ["Gold benefits", "Program naming opportunity", "Year-round co-branding"] },
  ];
  return (
    <div data-testid="sponsorship-page">
      <PageHeader eyebrow="Sponsorship" title="Partner with BCOL." subtitle="Sponsors help us scale programs, host signature events, and serve more families. Partnerships are tailored to your goals." />
      <PartnersStrip title="Trusted by our partners" subtitle="A heartfelt thank-you to the organizations and individuals who make our work possible." compact />
      <Section>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
          {tiers.map((t) => (
            <div key={t.name} data-testid={`sponsor-tier-${t.name.toLowerCase()}`}
              className={`rounded-2xl p-7 border ${t.highlight ? "bg-[var(--bcol-navy)] text-white border-[var(--bcol-navy)] shadow-xl" : "bg-white border-[var(--bcol-border)]"}`}>
              <div className={`eyebrow ${t.highlight ? "text-[var(--bcol-gold)]" : ""}`}>{t.name}</div>
              <div className={`font-display text-4xl mt-2 ${t.highlight ? "text-[var(--bcol-gold)]" : "text-[var(--bcol-navy)]"}`}>{t.price}</div>
              <ul className={`mt-5 space-y-2 text-sm ${t.highlight ? "text-white/85" : "text-[var(--bcol-muted)]"}`}>
                {t.perks.map((p) => <li key={p}>• {p}</li>)}
              </ul>
            </div>
          ))}
        </div>

        <form onSubmit={(e) => { e.preventDefault(); submit(f); }} className="max-w-3xl mx-auto card-soft p-8 space-y-5">
          <h3 className="font-display text-2xl text-[var(--bcol-navy)]">Express your interest</h3>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Company *"><Input required value={f.company_name} onChange={(e) => setF({ ...f, company_name: e.target.value })} data-testid="sponsor-company" /></Field>
            <Field label="Contact name *"><Input required value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} data-testid="sponsor-contact" /></Field>
            <Field label="Email *"><Input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="sponsor-email" /></Field>
            <Field label="Phone"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="sponsor-phone" /></Field>
            <Field label="Tier of interest">
              <select className="w-full bg-white border border-[var(--bcol-border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--bcol-navy)]"
                value={f.tier} onChange={(e) => setF({ ...f, tier: e.target.value })} data-testid="sponsor-tier">
                <option>Bronze</option><option>Silver</option><option>Gold</option><option>Platinum</option><option>Custom</option>
              </select>
            </Field>
          </div>
          <Field label="Tell us about your goals"><Textarea rows={3} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="sponsor-message" /></Field>
          <button disabled={loading || done} className="btn-primary" data-testid="sponsor-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {done ? "Submitted" : loading ? "Submitting..." : "Start the conversation"}
          </button>
        </form>
      </Section>
    </div>
  );
}
